import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  Settings,
  User,
  Mail,
  MessageSquare,
  Phone,
  Key,
  Bell,
  Save,
  X,
  Eye,
  EyeOff,
  Users,
  UserPlus,
  Trash2,
  Shield
} from 'lucide-react';

type SettingsTab = 'account' | 'users' | 'integrations' | 'notifications';
type IntegrationModal = 'email' | 'sms' | 'ai_call' | 'api_keys' | null;

interface MarketingUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  company_ids: string[];
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export default function SettingsPage() {
  const { user, marketingUser } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState<IntegrationModal>(null);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [users, setUsers] = useState<MarketingUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [userSettings, setUserSettings] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: '',
  });

  // Integration settings
  const [emailConfig, setEmailConfig] = useState({
    provider: 'resend',
    resend_api_key: '',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_password: '',
  });

  const [smsConfig, setSmsConfig] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
  });

  const [aiCallConfig, setAiCallConfig] = useState({
    elevenlabs_api_key: '',
    default_voice_id: '',
  });

  const [googleAdsConfig, setGoogleAdsConfig] = useState({
    company: 'hales',
    customer_id: '',
    account_name: '',
  });

  const [apiKeys, setApiKeys] = useState({
    openai_api_key: '',
    replicate_api_key: '',
    anthropic_api_key: '',
  });

  useEffect(() => {
    loadSettings();
    if (marketingUser?.role === 'marketing_admin') {
      loadUsers();
    }
  }, [marketingUser]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('marketing_users')
        .select('email, first_name, last_name, role')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      if (data) setUserSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from('marketing_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { error } = await supabase
        .from('marketing_users')
        .update({
          first_name: userSettings.first_name,
          last_name: userSettings.last_name,
        })
        .eq('id', user.id);

      if (error) throw error;
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleAdsMapping = async () => {
    try {
      setLoading(true);
      if (!googleAdsConfig.customer_id || !googleAdsConfig.company) {
        toast.error('Company and Customer ID are required');
        return;
      }
      const { error } = await supabase
        .from('google_ads_customer_mapping')
        .upsert(
          {
            company: googleAdsConfig.company,
            customer_id: googleAdsConfig.customer_id,
            account_name: googleAdsConfig.account_name || null,
            is_active: true,
          },
          { onConflict: 'company' }
        );
      if (error) throw error;
      toast.success('Google Ads account mapped successfully');
    } catch (e) {
      console.error('Error saving Google Ads mapping:', e);
      toast.error('Failed to save Google Ads mapping');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'account' as SettingsTab, label: 'Account', icon: User },
    ...(marketingUser?.role === 'marketing_admin' 
      ? [{ id: 'users' as SettingsTab, label: 'Users', icon: Users }] 
      : []
    ),
    { id: 'integrations' as SettingsTab, label: 'Integrations', icon: Key },
    { id: 'notifications' as SettingsTab, label: 'Notifications', icon: Bell },
  ];

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="w-8 h-8" />
          Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage your account and system preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'users' && marketingUser?.role === 'marketing_admin' && (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">User Management</h2>
                <button
                  onClick={() => setShowAddUserModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Add New User
                </button>
              </div>

              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Companies</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {u.first_name} {u.last_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              u.role === 'marketing_admin' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {u.role === 'marketing_admin' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {u.company_ids?.join(', ') || 'All'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'account' && (
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Account Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input type="email" value={userSettings.email} disabled className="input-field bg-gray-50" />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={userSettings.first_name}
                      onChange={(e) => setUserSettings({ ...userSettings, first_name: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={userSettings.last_name}
                      onChange={(e) => setUserSettings({ ...userSettings, last_name: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <input type="text" value={userSettings.role} disabled className="input-field bg-gray-50" />
                  <p className="text-xs text-gray-500 mt-1">Contact admin to change your role</p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Integrations & API Keys</h2>
              
              <div className="space-y-6">
                {/* Email Integration */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Email Service</h3>
                  </div>
                  <p className="text-gray-600 mb-4">Configure email delivery through Resend or SMTP</p>
                  <button onClick={() => setActiveModal('email')} className="btn-secondary">Configure Email</button>
                </div>

                {/* SMS Integration */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageSquare className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">SMS (Twilio)</h3>
                  </div>
                  <p className="text-gray-600 mb-4">Send SMS campaigns via Twilio</p>
                  <button onClick={() => setActiveModal('sms')} className="btn-secondary">Configure SMS</button>
                </div>

                {/* AI Calls */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Phone className="w-6 h-6 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">AI Calls (ElevenLabs)</h3>
                  </div>
                  <p className="text-gray-600 mb-4">AI-powered voice calls for automated outreach</p>
                  <button onClick={() => setActiveModal('ai_call')} className="btn-secondary">Configure AI Calls</button>
                </div>

                {/* Google Ads (Customer Mapping) */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-red-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Google Ads</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Map a Google Ads customer ID to your brand to enable the Google Ads pages.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                      <select
                        value={googleAdsConfig.company}
                        onChange={(e) => setGoogleAdsConfig({ ...googleAdsConfig, company: e.target.value as any })}
                        className="input w-full"
                      >
                        <option value="hales">Hales</option>
                        <option value="myccs">My CCS</option>
                        <option value="abraham">Abraham AC</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Customer ID</label>
                      <input
                        type="text"
                        value={googleAdsConfig.customer_id}
                        onChange={(e) => setGoogleAdsConfig({ ...googleAdsConfig, customer_id: e.target.value })}
                        placeholder="123-456-7890"
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Name (optional)</label>
                      <input
                        type="text"
                        value={googleAdsConfig.account_name}
                        onChange={(e) => setGoogleAdsConfig({ ...googleAdsConfig, account_name: e.target.value })}
                        placeholder="Ads Account Name"
                        className="input w-full"
                      />
                    </div>
                  </div>
                  <button onClick={saveGoogleAdsMapping} className="btn-secondary">Save Google Ads Mapping</button>
                </div>

                {/* API Keys */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Key className="w-6 h-6 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">API Keys</h3>
                  </div>
                  <p className="text-gray-600 mb-4">Manage API keys for OpenAI, Replicate, and other services</p>
                  <button onClick={() => setActiveModal('api_keys')} className="btn-secondary">Manage API Keys</button>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> API keys and integration credentials should be configured in your .env file or environment variables for security.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Notification Preferences</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">Campaign Launches</p>
                        <p className="text-sm text-gray-500">Get notified when campaigns start</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">Campaign Completions</p>
                        <p className="text-sm text-gray-500">Get notified when campaigns complete</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">Daily Summary</p>
                        <p className="text-sm text-gray-500">Receive a daily summary of campaign performance</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">Error Alerts</p>
                        <p className="text-sm text-gray-500">Get notified when campaigns encounter errors</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Notifications</h3>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">System Updates</p>
                        <p className="text-sm text-gray-500">Important system announcements and updates</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" className="mt-1 rounded border-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900">Feature Announcements</p>
                        <p className="text-sm text-gray-500">News about new features and improvements</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <button className="btn-primary flex items-center gap-2">
                    <Save className="w-5 h-5" />
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Modals */}
      {activeModal === 'email' && (
        <ConfigModal
          title="Email Configuration"
          onClose={() => setActiveModal(null)}
          onSave={() => {
            toast.success('Email configuration saved');
            setActiveModal(null);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
              <select
                value={emailConfig.provider}
                onChange={(e) => setEmailConfig({ ...emailConfig, provider: e.target.value })}
                className="input w-full"
              >
                <option value="resend">Resend</option>
                <option value="smtp">SMTP</option>
              </select>
            </div>

            {emailConfig.provider === 'resend' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resend API Key</label>
                <input
                  type="password"
                  value={emailConfig.resend_api_key}
                  onChange={(e) => setEmailConfig({ ...emailConfig, resend_api_key: e.target.value })}
                  placeholder="re_..."
                  className="input w-full"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                    <input
                      type="text"
                      value={emailConfig.smtp_host}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
                    <input
                      type="text"
                      value={emailConfig.smtp_port}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: e.target.value })}
                      placeholder="587"
                      className="input w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <input
                    type="text"
                    value={emailConfig.smtp_user}
                    onChange={(e) => setEmailConfig({ ...emailConfig, smtp_user: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={emailConfig.smtp_password}
                    onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </>
            )}
          </div>
        </ConfigModal>
      )}

      {activeModal === 'sms' && (
        <ConfigModal
          title="Twilio SMS Configuration"
          onClose={() => setActiveModal(null)}
          onSave={() => {
            toast.success('SMS configuration saved');
            setActiveModal(null);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account SID</label>
              <input
                type="text"
                value={smsConfig.twilio_account_sid}
                onChange={(e) => setSmsConfig({ ...smsConfig, twilio_account_sid: e.target.value })}
                placeholder="AC..."
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Auth Token</label>
              <input
                type="password"
                value={smsConfig.twilio_auth_token}
                onChange={(e) => setSmsConfig({ ...smsConfig, twilio_auth_token: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="text"
                value={smsConfig.twilio_phone_number}
                onChange={(e) => setSmsConfig({ ...smsConfig, twilio_phone_number: e.target.value })}
                placeholder="+1234567890"
                className="input w-full"
              />
            </div>
          </div>
        </ConfigModal>
      )}

      {activeModal === 'ai_call' && (
        <ConfigModal
          title="ElevenLabs AI Call Configuration"
          onClose={() => setActiveModal(null)}
          onSave={() => {
            toast.success('AI Call configuration saved');
            setActiveModal(null);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ElevenLabs API Key</label>
              <input
                type="password"
                value={aiCallConfig.elevenlabs_api_key}
                onChange={(e) => setAiCallConfig({ ...aiCallConfig, elevenlabs_api_key: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Voice ID</label>
              <input
                type="text"
                value={aiCallConfig.default_voice_id}
                onChange={(e) => setAiCallConfig({ ...aiCallConfig, default_voice_id: e.target.value })}
                placeholder="Voice ID from ElevenLabs"
                className="input w-full"
              />
            </div>
          </div>
        </ConfigModal>
      )}

      {activeModal === 'api_keys' && (
        <ConfigModal
          title="API Keys"
          onClose={() => setActiveModal(null)}
          onSave={() => {
            toast.success('API keys saved');
            setActiveModal(null);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">OpenAI API Key</label>
              <div className="relative">
                <input
                  type={showApiKey['openai'] ? 'text' : 'password'}
                  value={apiKeys.openai_api_key}
                  onChange={(e) => setApiKeys({ ...apiKeys, openai_api_key: e.target.value })}
                  placeholder="sk-..."
                  className="input w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey({ ...showApiKey, openai: !showApiKey['openai'] })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey['openai'] ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Replicate API Token</label>
              <div className="relative">
                <input
                  type={showApiKey['replicate'] ? 'text' : 'password'}
                  value={apiKeys.replicate_api_key}
                  onChange={(e) => setApiKeys({ ...apiKeys, replicate_api_key: e.target.value })}
                  placeholder="r8_..."
                  className="input w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey({ ...showApiKey, replicate: !showApiKey['replicate'] })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey['replicate'] ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Anthropic API Key</label>
              <div className="relative">
                <input
                  type={showApiKey['anthropic'] ? 'text' : 'password'}
                  value={apiKeys.anthropic_api_key}
                  onChange={(e) => setApiKeys({ ...apiKeys, anthropic_api_key: e.target.value })}
                  placeholder="sk-ant-..."
                  className="input w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey({ ...showApiKey, anthropic: !showApiKey['anthropic'] })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey['anthropic'] ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Security Note:</strong> API keys are stored securely in your environment. Never share these keys or commit them to version control.
              </p>
            </div>
          </div>
        </ConfigModal>
      )}

      {showAddUserModal && <AddUserModal onClose={() => {
        setShowAddUserModal(false);
        loadUsers();
      }} />}
    </div>
  );
}

// Add User Modal Component
function AddUserModal({ onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'marketing_user',
    companyIds: [] as string[],
  });

  const companies = [
    { id: 'halesac', name: 'Hales AC' },
    { id: 'myccs', name: 'My CCS' },
    { id: 'abrahamac', name: 'Abraham AC' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Retrieve current session token for Authorization header
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('Not authenticated. Please sign in again.');
      }

      // Prefer configurable API base, fallback to local dev port
      const API_BASE = (import.meta as any).env?.VITE_MARKETING_API_URL || 'http://localhost:3004';

      const res = await fetch(`${API_BASE}/api/marketing/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          companyIds: formData.companyIds.length > 0 ? formData.companyIds : ['halesac'],
        }),
      });

      if (!res.ok) {
        let message = 'Failed to create user';
        try {
          const body = await res.json();
          message = body?.error || message;
        } catch {
          // ignore json parse error
        }
        throw new Error(message);
      }

      toast.success(`User ${formData.email} created successfully`);
      onClose();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (companyId: string) => {
    setFormData(prev => ({
      ...prev,
      companyIds: prev.companyIds.includes(companyId)
        ? prev.companyIds.filter(id => id !== companyId)
        : [...prev.companyIds, companyId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Add New User</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="input w-full"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="input w-full"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input w-full"
                placeholder="user@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input w-full"
                placeholder="Minimum 8 characters"
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="input w-full"
              >
                <option value="marketing_user">Marketing User</option>
                <option value="marketing_admin">Marketing Admin</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Admins can manage users and have full access
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Access
              </label>
              <div className="space-y-2 border border-gray-200 rounded-lg p-4">
                {companies.map((company) => (
                  <label key={company.id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.companyIds.includes(company.id)}
                      onChange={() => toggleCompany(company.id)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{company.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to grant access to all companies
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Configuration Modal Component
function ConfigModal({
  title,
  children,
  onClose,
  onSave,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {children}

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="btn-primary"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
